<script setup>
/**
 * One case in the queue: what it is, where it is, who has it, who can see it,
 * and when it last moved.
 *
 * The whole row is the target — clicking anywhere in it opens the workroom —
 * but the title is also a real link, so the row is reachable with Tab and
 * opens with Enter without a keydown handler pretending to be one. Every
 * column renders through the T-18 kit, so a stage or an owner looks the same
 * here as it does in the workroom header.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import CaseTypeBadge from './CaseTypeBadge.vue';
import StagePill from './StagePill.vue';
import OwnerChip from './OwnerChip.vue';
import VisibilityBadge from './VisibilityBadge.vue';
import {
  lastActivityLabel,
  presentOwner,
} from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  supportCase: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const owner = computed(() => presentOwner(props.supportCase));

const lastActivity = computed(() =>
  lastActivityLabel(props.supportCase.updatedAt)
);

const linkTo = computed(() => ({
  name: 'support_cases_show',
  params: { displayId: String(props.supportCase.displayId) },
}));
</script>

<template>
  <BaseTableRow
    :item="supportCase"
    class="transition-colors cursor-pointer hover:bg-n-alpha-1 focus-within:bg-n-alpha-1"
    :data-testid="`case-row-${supportCase.displayId}`"
    @click="emit('select', supportCase)"
  >
    <template #default>
      <!-- The title column is the one that may take the leftover width: it is
           given half the table and a floor, and every other column shrinks to
           its own content (`w-px` + `whitespace-nowrap`). Without this the
           auto-layout table hands the width to the pills and the title
           collapses to an ellipsis. -->
      <BaseTableCell class="w-1/2 min-w-[16rem]">
        <div class="flex items-center min-w-0 gap-2">
          <span
            class="i-lucide-ticket size-4 shrink-0 text-n-slate-10"
            aria-hidden="true"
          />
          <router-link
            :to="linkTo"
            class="min-w-0 truncate text-body-main text-n-slate-12 hover:underline"
          >
            {{ supportCase.title }}
          </router-link>
          <span class="shrink-0 tabular-nums text-n-slate-10">
            {{
              t('SUPPORT_CASES.LIST.DISPLAY_ID', { id: supportCase.displayId })
            }}
          </span>
        </div>
      </BaseTableCell>

      <BaseTableCell class="w-px whitespace-nowrap">
        <CaseTypeBadge
          v-if="supportCase.caseType"
          :case-type="supportCase.caseType"
        />
      </BaseTableCell>

      <BaseTableCell class="w-px whitespace-nowrap">
        <StagePill
          v-if="supportCase.caseStage"
          :stage="supportCase.caseStage"
        />
      </BaseTableCell>

      <BaseTableCell class="w-px whitespace-nowrap">
        <OwnerChip :owner="owner" />
      </BaseTableCell>

      <BaseTableCell class="w-px whitespace-nowrap">
        <VisibilityBadge
          :mode="supportCase.visibility?.mode"
          :team-names="supportCase.visibility?.teamNames || []"
        />
      </BaseTableCell>

      <BaseTableCell class="w-px whitespace-nowrap">
        <span class="whitespace-nowrap text-body-main text-n-slate-11">
          {{ lastActivity }}
        </span>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
